import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Briefcase, Building2, FileText, Home, Users, Wrench, type LucideIcon } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useCrm } from "@/hooks/useCrm";
import actionsJson from "@shared/actions.json";
import { str } from "@/lib/crm";
import { boardNavigation, NAVIGATION, recordPath } from "@/lib/navigation";
import { matchWords } from "@/lib/search";
import { PERMISSIONS } from "@/lib/schema";
import type { CrmRecord, TableKey } from "@/lib/types";

// Zoeken (Ctrl+K): panden, bedrijven, contacten, woonvragen, contracten en tickets, de schermen
// en de acties die je mag uitvoeren. Een record opent het scherm waar het thuishoort.

interface Item {
  key: string;
  label: string;
  sub: string;
  type: string;
  icon: LucideIcon;
  run: () => void;
}

export function SearchDialog({ open, onOpenChange, onAction }: { open: boolean; onOpenChange: (open: boolean) => void; onAction: (actionId: string) => void }) {
  const crm = useCrm();
  const navigate = useNavigate();

  const groups = useMemo(() => {
    if (!open) return [];
    const go = (path: string) => () => navigate(path);
    const join = (...parts: string[]) => parts.filter(Boolean).join(" · ");
    const records = (table: TableKey, type: string, icon: LucideIcon, label: (r: CrmRecord) => string, sub: (r: CrmRecord) => string): Item[] =>
      crm.all(table).map((r) => ({ key: `${table}:${r.id}`, label: label(r) || "(naamloos)", sub: sub(r), type, icon, run: go(recordPath(crm, table, r.id)) }));
    const actions = actionsJson.actions as Record<string, { label: string; module: string }>;
    return [
      {
        heading: "Dossiers",
        items: [
          ...records("properties", "Pand", Building2, (r) => str(r, "Adres"), (r) => join(str(r, "Stad"), str(r, "Status"), crm.names("contacts", r.fields.Eigenaar))),
          ...records("companies", "Bedrijf", Briefcase, (r) => str(r, "Naam"), (r) => join(str(r, "Contactpersoon"), str(r, "Status"))),
          ...records("contacts", "Contact", Users, (r) => str(r, "Naam"), (r) => join(str(r, "Rol"), str(r, "E-mail"), crm.names("companies", r.fields.Bedrijf))),
          ...records("requests", "Woonvraag", Home, (r) => str(r, "Aanvrager"), (r) => join(str(r, "Fase"), str(r, "Regio"))),
          ...records("contracts", "Contract", FileText, (r) => str(r, "Nummer"), (r) => join(crm.names("contacts", r.fields.Huurder), crm.names("properties", r.fields.Pand), str(r, "Fase"))),
          ...records("tickets", "Ticket", Wrench, (r) => join(str(r, "Nummer"), str(r, "Omschrijving")), (r) => join(crm.names("properties", r.fields.Pand), str(r, "Status")))
        ]
      },
      {
        heading: "Schermen",
        items: [...NAVIGATION, ...(boardNavigation(crm) ? [boardNavigation(crm)!] : [])]
          .flatMap((group) => group.items)
          .filter((item) => !item.permission || crm.can(item.permission))
          .map<Item>((item) => ({ key: `nav:${item.to}`, label: item.label, sub: "Scherm", type: "Scherm", icon: item.icon, run: go(item.to) }))
      },
      {
        heading: "Acties",
        items: Object.entries(actions)
          .filter(([id]) => crm.can(PERMISSIONS.actions[id] ?? ""))
          .map<Item>(([id, a]) => ({ key: `action:${id}`, label: a.label, sub: a.module, type: "Actie", icon: Activity, run: () => onAction(id) }))
      }
    ];
  }, [open, crm, navigate, onAction]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12vh] max-w-[640px] translate-y-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-top-0 [&>button]:hidden" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Zoeken</DialogTitle>
        <Command filter={matchWords}>
          <CommandInput placeholder="Zoek een pand, persoon, bedrijf, contract, scherm of actie…" />
          <CommandList>
            <CommandEmpty>Geen resultaten. Probeer een adres, naam, nummer of status.</CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.heading} heading={group.heading}>
                {group.items.map((item) => (
                  <CommandItem
                    key={item.key}
                    value={item.key}
                    keywords={[item.label, item.sub, item.type]}
                    onSelect={() => {
                      onOpenChange(false);
                      item.run();
                    }}
                  >
                    <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-surface-warm text-navy-800">
                      <item.icon className="size-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{item.label}</span>
                      {item.sub && <span className="block truncate text-[11px] text-muted-foreground">{item.sub}</span>}
                    </span>
                    <span className="shrink-0 rounded-full bg-surface-soft px-2 py-0.5 text-[10px] font-bold text-muted-foreground">{item.type}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
